import { useState, useMemo } from 'react';
import type { SortingState, PaginationState } from '@hs/ui';
import { Button, Chip } from '@heroui/react';
import { IconRefresh as RefreshCw } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { PageDataGrid, PageDataGridActionToolbar } from '@/components/layouts/page/PageDataGrid';

type Order = {
  id: string;
  code?: string;
  type?: string;
  originalAmount?: string | number;
  discountAmount?: string | number;
  taxAmount?: string | number;
  payableAmount?: string | number;
  paidAmount?: string | number;
  status: string;
  createdAt?: string;
};

const ORDER_SORT_FIELDS = new Set([
  'code',
  'type',
  'status',
  'payableAmount',
  'createdAt',
  'paidAt',
  'updatedAt',
]);

const STATUS_MAP: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'danger' }> = {
  PENDING_PAYMENT: { label: '待支付', color: 'warning' },
  PAID: { label: '已支付', color: 'success' },
  CANCELED: { label: '已取消', color: 'default' },
  PARTIALLY_REFUNDED: { label: '部分退款', color: 'default' },
  REFUNDED: { label: '已退款', color: 'default' },
};

function formatFen(v: string | number | undefined) {
  const n = typeof v === 'string' ? Number(v) : (v ?? 0);
  if (Number.isNaN(n)) return '—';
  return `¥${(n / 100).toFixed(2)}`;
}

function formatDate(dateString: string | null | undefined) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function OrdersListPanel() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const sortItem = sorting[0];
  const sortBy =
    sortItem && ORDER_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as
          | 'code'
          | 'type'
          | 'status'
          | 'payableAmount'
          | 'createdAt'
          | 'paidAt'
          | 'updatedAt')
      : undefined;

  const { data, isLoading, refetch } = trpc.bizOrder.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortBy
        ? { sortBy, sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const orders = (data?.items ?? []) as Order[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const columnOverrides = useMemo(
    () => ({
      originalAmount: { cell: (row: Order) => formatFen(row.originalAmount) },
      discountAmount: { cell: (row: Order) => formatFen(row.discountAmount) },
      taxAmount: { cell: (row: Order) => formatFen(row.taxAmount) },
      payableAmount: { cell: (row: Order) => formatFen(row.payableAmount) },
      paidAmount: { cell: (row: Order) => formatFen(row.paidAmount) },
      status: {
        cell: (row: Order) => {
          const s = STATUS_MAP[row.status] ?? { label: row.status, color: 'default' as const };
          return (
            <Chip color={s.color} size="sm" variant="soft">
              <Chip.Label>{s.label}</Chip.Label>
            </Chip>
          );
        },
      },
      createdAt: { cell: (row: Order) => formatDate(row.createdAt) },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <PageDataGrid<Order>
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        source="sales_order"
        data={orders}
        recordCount={total}
        pageCount={pageCount}
        isLoading={isLoading}
        emptyMessage="暂无数据"
        getRowId={(row) => row.id}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnOverrides={columnOverrides}
        metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
        paginationBar={{}}
      >
        <PageDataGridActionToolbar>
          <Button variant="secondary" onPress={() => void refetch()}>
            <RefreshCw className="size-4" stroke={1.5} />
            刷新
          </Button>
        </PageDataGridActionToolbar>
      </PageDataGrid>
    </div>
  );
}
